import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { Loader2, Search, Building2, Tag, FileCheck2, FileWarning, Send, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// States that belong on the Pipeline page (post-approval, pre-customer)
const PIPELINE_STATES = ['APPROVED', 'ACCOUNTING_APPROVED', 'CONTRACT_SENT'];

const STAGE_META = {
  APPROVED:            { label: 'APPROVED',            color: 'text-emerald-300', bg: 'bg-emerald-900/20', border: 'border-emerald-700/50', icon: CheckCircle2 },
  ACCOUNTING_APPROVED: { label: 'ACCOUNTING APPROVED', color: 'text-teal-300',    bg: 'bg-teal-900/20',    border: 'border-teal-700/50',    icon: CheckCircle2 },
  CONTRACT_SENT:       { label: 'CONTRACT SENT',       color: 'text-cyan-300',    bg: 'bg-cyan-900/20',    border: 'border-cyan-700/50',    icon: Send },
};

const TENANT_BADGES = {
  MM: { label: 'MM', color: 'text-emerald-400 bg-emerald-900/20 border-emerald-700/50' },
  MT: { label: 'MT', color: 'text-violet-400 bg-violet-900/20 border-violet-700/50' },
};

const STALE_THRESHOLD_DAYS = 14;

function daysSince(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export default function Pipeline() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [tenantFilter, setTenantFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [actionId, setActionId] = useState(null);

  useEffect(() => { load(); }, [search, tenantFilter, stageFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        state: stageFilter || PIPELINE_STATES.join(','),
        limit: 200,
      };
      if (search) params.search = search;
      if (tenantFilter) params.tenant_group = tenantFilter;
      const res = await api.get('/customers', { params });
      setLeads(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const advance = async (lead, newState, note = '') => {
    setActionId(lead.id);
    try {
      await api.patch(`/customers/${lead.id}/onboarding`, { new_state: newState, note });
      toast.success(
        newState === 'CONTRACT_SIGNED'
          ? `${lead.company_name} moved to Customers ✓`
          : `Updated to ${newState.replace(/_/g, ' ')}`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setActionId(null);
    }
  };

  // Group counts
  const counts = leads.reduce((acc, l) => {
    acc[l.onboarding_state] = (acc[l.onboarding_state] || 0) + 1;
    return acc;
  }, {});
  const staleCount = leads.filter(l => (daysSince(l.created_at) || 0) > STALE_THRESHOLD_DAYS).length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// SALES PIPELINE</p>
          <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>Pipeline</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {leads.length} approved {leads.length === 1 ? 'lead' : 'leads'} awaiting contract signature
          </p>
        </div>
        {staleCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md border" style={{ background: 'var(--warning-muted)', borderColor: 'var(--warning)' }} data-testid="stale-alert">
            <AlertTriangle size={14} style={{ color: 'var(--warning)' }} />
            <span className="text-xs font-bold font-['Manrope']" style={{ color: 'var(--warning)' }}>
              {staleCount} stale {staleCount === 1 ? 'lead' : 'leads'} ({'>'}{STALE_THRESHOLD_DAYS} days)
            </span>
          </div>
        )}
      </div>

      {/* Stage strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PIPELINE_STATES.map(s => {
          const meta = STAGE_META[s];
          const Icon = meta.icon;
          const active = stageFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStageFilter(active ? '' : s)}
              data-testid={`pipeline-stage-${s}`}
              className={`card-slate p-4 text-left transition-all ${active ? 'ring-2' : ''}`}
              style={{ ringColor: active ? 'var(--primary)' : 'transparent' }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${meta.color} ${meta.bg} ${meta.border}`}>
                  <Icon size={10} />
                  {meta.label}
                </span>
                <span className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{counts[s] || 0}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full input-slate pl-9"
            placeholder="Search by company name..." data-testid="pipeline-search" />
        </div>
        <select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}
          className="select-slate min-w-[120px]" data-testid="tenant-filter">
          <option value="">All Tenants</option>
          <option value="MM">MM Only</option>
          <option value="MT">MT Only</option>
        </select>
        {stageFilter && (
          <button onClick={() => setStageFilter('')}
            className="text-xs underline" style={{ color: 'var(--text-muted)' }}
            data-testid="clear-stage-filter">
            Clear stage filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16">
            <FileCheck2 size={36} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>
              No leads in the pipeline right now. Approved intakes will appear here.
            </p>
          </div>
        ) : (
          <table className="w-full table-comfortable" data-testid="pipeline-table">
            <thead>
              <tr>
                {[
                  { label: 'Tenant', cls: '' },
                  { label: 'Company', cls: '' },
                  { label: 'Contact', cls: '' },
                  { label: 'Stage', cls: '' },
                  { label: 'Days In Pipeline', cls: 'hidden md:table-cell num' },
                  { label: 'Actions', cls: '' },
                ].map((h, i) => (
                  <th key={i} className={h.cls}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => {
                const tenantBadge = TENANT_BADGES[lead.tenant_group] || { label: lead.tenant_group || 'N/A', color: 'text-gray-400 bg-gray-900/20 border-gray-700/50' };
                const stage = STAGE_META[lead.onboarding_state];
                const StageIcon = stage?.icon || Clock;
                const days = daysSince(lead.created_at);
                const isStale = (days || 0) > STALE_THRESHOLD_DAYS;
                return (
                  <tr key={lead.id} data-testid={`pipeline-row-${lead.id}`}
                    className="cursor-pointer"
                    onClick={() => navigate(`/ops/customers/${lead.id}`)}>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${tenantBadge.color}`}>
                        <Tag size={10} />
                        {tenantBadge.label}
                      </span>
                    </td>
                    <td className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{lead.company_name}</span>
                        {isStale && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border font-['JetBrains_Mono'] text-red-300 bg-red-900/20 border-red-700/50"
                            title={`${days} days in pipeline`} data-testid={`stale-${lead.id}`}>
                            <FileWarning size={9} /> STALE {days}d
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{lead.contact_email || '—'}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${stage?.color || ''} ${stage?.bg || ''} ${stage?.border || 'border-[var(--border)]'}`}>
                        <StageIcon size={10} />
                        {stage?.label || lead.onboarding_state?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="hidden md:table-cell num font-['JetBrains_Mono']" style={{ color: isStale ? 'var(--error)' : 'var(--text-muted)' }}>
                      {days !== null ? `${days}d` : '—'}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {lead.onboarding_state === 'ACCOUNTING_APPROVED' && (
                          <button
                            onClick={() => advance(lead, 'CONTRACT_SENT', 'Contract sent to customer')}
                            disabled={actionId === lead.id}
                            data-testid={`send-contract-${lead.id}`}
                            className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md border transition-colors"
                            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-muted)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            {actionId === lead.id ? <Loader2 size={11} className="animate-spin" /> : <><Send size={10} className="inline mr-1" /> Mark Contract Sent</>}
                          </button>
                        )}
                        {(lead.onboarding_state === 'CONTRACT_SENT' || lead.onboarding_state === 'ACCOUNTING_APPROVED' || lead.onboarding_state === 'APPROVED') && (
                          <button
                            onClick={() => advance(lead, 'CONTRACT_SIGNED', 'Contract signed — promoted to active customer')}
                            disabled={actionId === lead.id}
                            data-testid={`sign-contract-${lead.id}`}
                            className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md text-white"
                            style={{ background: 'var(--success)' }}>
                            {actionId === lead.id ? <Loader2 size={11} className="animate-spin" /> : <><FileCheck2 size={10} className="inline mr-1" /> Contract Signed</>}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
